import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createSprint, SprintType } from '../../../store/sprintSlice';

interface Props {
  projectId?: string;
  onCreated?: (sprintId: string) => void;
}

const SPRINT_TYPES: { value: SprintType; label: string; description: string }[] = [
  { value: 'strict', label: 'Strict', description: 'Fixed dates, capacity enforced. Only Sprint Owner & Editors can add work.' },
  { value: 'fluid', label: 'Fluid', description: 'Optional dates, no capacity enforcement. Members manage their own work.' },
];

const SprintForm: React.FC<Props> = ({ projectId: projectIdProp, onCreated }) => {
  const params = useParams<{ id: string }>();
  const projectId = projectIdProp || params.id || '';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector(s => s.sprints);

  const [form, setForm] = useState({
    name: '',
    description: '',
    sprintType: 'strict' as SprintType,
    capacity: '',
    startDate: '',
    endDate: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(createSprint({
      projectId,
      name: form.name,
      description: form.description || undefined,
      sprintType: form.sprintType,
      capacity: form.capacity ? parseInt(form.capacity) : undefined,
      startDate: form.startDate,
      endDate: form.endDate || undefined,
    } as any));

    if (createSprint.fulfilled.match(result)) {
      const sprint = result.payload;
      onCreated ? onCreated(sprint._id) : navigate(`/projects/${projectId}/sprints/${sprint._id}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">New Sprint</h2>

      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Sprint Name *</label>
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          required
          placeholder="Sprint 1 — Auth & User Management"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          rows={3}
          placeholder="What is the goal of this sprint?"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Sprint Type *</label>
        <div className="grid grid-cols-2 gap-2">
          {SPRINT_TYPES.map(t => (
            <label
              key={t.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                form.sprintType === t.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="sprintType"
                value={t.value}
                checked={form.sprintType === t.value}
                onChange={handleChange}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">{t.label}</div>
                <div className="text-xs text-gray-500">{t.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
          <input
            type="date"
            name="startDate"
            value={form.startDate}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            name="endDate"
            value={form.endDate}
            onChange={handleChange}
            min={form.startDate}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Capacity <span className="text-gray-400 font-normal">(story points)</span>
        </label>
        <input
          type="number"
          name="capacity"
          value={form.capacity}
          onChange={handleChange}
          min={1}
          placeholder="e.g. 40"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Creating…' : 'Create Sprint'}
        </button>
      </div>
    </form>
  );
};

export default SprintForm;
